/**
 * Expression helpers for Numeric accepted answers (teacher client).
 *
 * A Numeric accepted answer is either a plain number (value-graded via
 * @showbie-socrative/socrative-utils's canonical value engine) or a math
 * expression authored with the equation editor (MathLive `latex-expanded`).
 * This module only classifies/normalizes for authoring — actual expression
 * grading lives in the grading services. Keep `isPlausibleExpression` in sync
 * with the server-side gate that will be restored at
 * services/teacher/refactor/modules/quizzes/utils/numericAnswers.ts (Phase 4).
 */

import {
  LATEX_FUNCTION_TOKENS,
  isNumericParseError,
  normalizeExpressionLatex,
  parseNumericInput,
} from '@showbie-socrative/socrative-utils';

// Promotion (`sqrt(x)` -> `\sqrt{x}`) is socrative-utils' shared
// implementation (SOC-6000): the grader in student-service runs the same one
// over the same token list, so an accepted value and a student answer can't
// read the same word differently. Re-exported so existing imports keep working.
export { normalizeExpressionLatex };

/** Accepted-answer expressions are capped well below abuse territory. */
export const MAX_EXPRESSION_LENGTH = 64;

/** UI cap on accepted forms per Numeric question. */
export const MAX_NUMERIC_ANSWERS = 10;

// Characters an accepted expression may contain. `%` is NUMBER-only:
// parseNumericInput handles `50%` and `50\%`, and inside an expression a
// percent has no clear meaning (`50%+x`). A comma is allowed: the math field
// writes a comma-locale decimal as `{,}` and an argument separator as a bare
// `,` (`\gcd(12,18)`, `(1,2)`), and the grader reads both the same way.
// `!` is factorial. Still EXCLUDES <>&= so the server's HTML-escaping can
// never corrupt a stored expression, and so a teacher typing an equation
// (x=1) is told to enter a value instead of an equation.
const EXPRESSION_CHARSET = /^[0-9a-zA-Z+\-*/^().,{}[\]_\\!\s]+$/;

// Numeric-token characters only (no letters, operators beyond a leading sign,
// brackets, or backslashes). A string built only from these characters that
// still fails parseNumericInput is a malformed/too-long NUMBER attempt (e.g.
// a 20-digit integer exceeding NUMERIC_MAX_INTEGER_DIGITS), not a plausible
// expression — EXPRESSION_CHARSET alone would wrongly accept it, since plain
// digits are valid expression characters too.
const NUMERIC_TOKEN_ONLY = /^[+-]?[0-9.,%\s]*$/;

// A bare comma separates two operands (`\gcd(12,18)`, `(1,2)`), so one with
// nothing on one side is a typo: `,5x`, `(1,)`, `x+,y`, `x,,y`. The operand
// after it can be signed, as in `(1,-2)`. `{,}` is the comma-locale decimal
// and `\,` a thin space, so neither counts.
const MISPLACED_COMMA = /(^|[([{,+\-*/^])\s*,|,\s*($|[)\]},*/^])/;

function hasMisplacedComma(text: string): boolean {
  const commas = text.replace(/\\[,;:! ]/g, ' ').replace(/\{,\}/g, '.');
  return MISPLACED_COMMA.test(commas);
}

// `!` is factorial only after an operand. A leading `!` is compute-engine's
// logical Not (`!5` -> ["Not", 5]), which never evaluates to a number.
// Postfix chains are fine: `3!!` is the double factorial.
const MISPLACED_FACTORIAL = /(^|[^0-9A-Za-z)\]}!])!/;

function bracketsBalanced(text: string): boolean {
  let paren = 0;
  let brace = 0;
  let bracket = 0;
  for (const ch of text) {
    if (ch === '(') paren++;
    else if (ch === ')') paren--;
    else if (ch === '{') brace++;
    else if (ch === '}') brace--;
    else if (ch === '[') bracket++;
    else if (ch === ']') bracket--;
    if (paren < 0 || brace < 0 || bracket < 0) {
      return false;
    }
  }
  return paren === 0 && brace === 0 && bracket === 0;
}

// Matches one already-LaTeX macro invocation: a command name, plus its
// single immediately-following brace group if it has one (`\operatorname{
// gcd}`, `\sqrt{x}`), or a lone command (`\pi`, `\left`), or an escaped
// single character (`\!`). One level of braces is enough for every macro
// the equation editor emits into a Numeric expression — nested braces don't
// occur here. Shared by hasUnknownWord and normalizeExpressionLatex so both
// treat "already a macro" the same way: as trusted equation-editor output
// to skip over whole, argument included, never as text to word-check or
// re-promote.
const LATEX_MACRO_PATTERN = '\\\\[a-zA-Z]+(?:\\{[^{}]*\\})?|\\\\.';
const LATEX_MACRO_GLOBAL = new RegExp(LATEX_MACRO_PATTERN, 'g');

// A run of two or more bare letters is only math if it names a function or
// constant (LATEX_FUNCTION_TOKENS). Single letters stay free as variables:
// `x`, `n`, `y`. Without this, `fcghbfb`, `hunk`, and the `test` in
// `test0123e2y382` all satisfy every other check and were previously
// announced as graded expressions (SOC-5959) even though they can never
// match a student's answer. Case-sensitive to match normalizeExpressionLatex
// exactly: `SIN(x)` has no upright preview to render, so it isn't accepted.
function hasUnknownWord(text: string): boolean {
  // Backslash macros (`\frac`, `\left`, `\operatorname{gcd}`) are the
  // equation editor's own output, not typed prose, and are intentionally
  // not validated here — strip each macro AND its argument before scanning
  // what the teacher wrote. Stripping only the command name and leaving the
  // argument behind would word-check inside it: `\operatorname{erf}` is how
  // the editor's function panel writes an arbitrary named function, and
  // "erf" alone isn't on LATEX_FUNCTION_TOKENS.
  const withoutMacros = text.replace(LATEX_MACRO_GLOBAL, ' ');
  const words = withoutMacros.match(/[a-zA-Z]{2,}/g) ?? [];
  return words.some((word) => !LATEX_FUNCTION_TOKENS.includes(word));
}

/**
 * Syntactic gate for expression accepted-answers: not proof of mathematical
 * validity, just enough to reject garbage/HTML/incomplete equations at
 * authoring time.
 */
export function isPlausibleExpression(text: string): boolean {
  const trimmed = text.trim();
  if (trimmed.length === 0 || trimmed.length > MAX_EXPRESSION_LENGTH) {
    return false;
  }
  if (!EXPRESSION_CHARSET.test(trimmed)) {
    return false;
  }
  if (!/[0-9a-zA-Z]/.test(trimmed)) {
    return false;
  }
  if (/[+\-*/^]$/.test(trimmed)) {
    return false;
  }
  if (hasMisplacedComma(trimmed)) {
    return false;
  }
  if (MISPLACED_FACTORIAL.test(trimmed)) {
    return false;
  }
  if (hasUnknownWord(trimmed)) {
    return false;
  }
  // MathLive inserts \placeholder{} for an empty slot (e.g. an unfilled
  // \sqrt[]{} from the virtual keyboard). It passes every check above but can
  // never match a student's answer, so it's rejected explicitly.
  if (trimmed.includes('\\placeholder')) {
    return false;
  }
  return bracketsBalanced(trimmed);
}

/** The three/four-state classification an accepted answer can be in. */
export type NumericAnswerKind = 'empty' | 'number' | 'expression' | 'invalid';

/**
 * Single source of truth for "is this accepted answer a number, an
 * expression, or invalid" — used by both the editor (for the inline
 * hint/error and preview) and QuizValidation (to block saving). Keeping this
 * logic in one place is deliberate: duplicating it at both call sites is
 * exactly how they drifted apart previously.
 */
export function classifyNumericAnswer(text: string): NumericAnswerKind {
  const trimmed = (text ?? '').trim();
  if (trimmed.length === 0) {
    return 'empty';
  }
  if (!isNumericParseError(parseNumericInput(trimmed))) {
    return 'number';
  }
  if (NUMERIC_TOKEN_ONLY.test(trimmed)) {
    return 'invalid';
  }
  return isPlausibleExpression(trimmed) ? 'expression' : 'invalid';
}

// Matches the math-blot envelope a Numeric answer could have been saved in
// before this phase: <span class="quill-math" data-latex="L">$$L$$</span>,
// possibly wrapped in a Quill <p>.
const DATA_LATEX_ATTR = /data-latex="([^"]*)"/;

function unescapeHtmlEntities(text: string): string {
  return text
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&');
}

// Delimiter envelopes an expression can arrive wrapped in — MathLive's own
// `text/plain` clipboard export is `$$ <latex> $$` (defaultExportHook), and a
// teacher can type the single/double-dollar or \(...\)/\[...\] forms by hand.
// Each entry returns the unwrapped body, or null if it doesn't match.
//
// Deliberately NOT here: `\text{...}` / `\mathrm{...}`. MathLive 0.80.0 only
// wraps pasted prose in \text{} when it contains an inner `$...$` pair, and
// `insert(..., { format: 'latex' })` skips that code path entirely — so the
// envelope this was written for never arrives. What it did catch was genuine
// text-mode content, where stripping the wrapper changes the meaning (inside
// \text{} a caret is a literal caret, not a superscript) and, worse, exposes
// the argument to hasUnknownWord: a stored `\text{erf}` flipped from
// `expression` to `invalid` on read, blocking the save of a question the
// teacher never touched.
type WrapperUnwrapper = (text: string) => string | null;

// Builds an unwrapper for a symmetric `open ... close` envelope that rejects
// (leaves whole) if the body itself contains another full `open...close`
// pair: `$$1$$ and $$2$$` would otherwise come back as the nonsense fragment
// `1$$ and $$2` under a naive greedy match. Applied uniformly to all four
// delimiter forms below — this guard previously only covered `$$...$$`/`$...$`.
function delimiterUnwrapper(open: string, close: string): WrapperUnwrapper {
  return (text) => {
    if (
      text.length < open.length + close.length ||
      !text.startsWith(open) ||
      !text.endsWith(close)
    ) {
      return null;
    }
    const body = text.slice(open.length, text.length - close.length);
    if (body.includes(open) || body.includes(close)) {
      return null;
    }
    return body.trim();
  };
}

const WRAPPER_PATTERNS: WrapperUnwrapper[] = [
  delimiterUnwrapper('$$', '$$'),
  delimiterUnwrapper('$', '$'),
  delimiterUnwrapper('\\(', '\\)'),
  delimiterUnwrapper('\\[', '\\]'),
];

/** Peel off at most a handful of nested delimiter envelopes. */
function unwrapEnvelopes(text: string): string {
  let current = text.trim();
  for (let i = 0; i < 5; i++) {
    let unwrapped: string | null = null;
    for (const unwrap of WRAPPER_PATTERNS) {
      unwrapped = unwrap(current);
      if (unwrapped !== null) {
        break;
      }
    }
    if (unwrapped === null) {
      break;
    }
    current = unwrapped;
  }
  return current;
}

// Superscript/subscript digits (and the handful of non-digit glyphs that
// round out the Unicode block) MathLive/browsers can produce from a copy of
// rendered math — a run collapses into one `^{...}`/`_{...}` group so `x²³`
// becomes `x^{23}`, not `x^{2}^{3}`.
// Digits 0-9 map to the same values in both scripts; only the trailing
// non-digit glyphs (superscript also has `ⁿ`) differ, so build both maps
// (and their run regexes, so the character class can never drift from the
// map) from one digit source instead of repeating the 0-9 mapping twice.
function glyphMap(
  digits: string,
  extra: Record<string, string>
): Record<string, string> {
  const map: Record<string, string> = { ...extra };
  Array.from(digits).forEach((glyph, i) => {
    map[glyph] = String(i);
  });
  return map;
}
const SUPERSCRIPT_MAP = glyphMap('⁰¹²³⁴⁵⁶⁷⁸⁹', { ⁿ: 'n', '⁺': '+', '⁻': '-' });
const SUBSCRIPT_MAP = glyphMap('₀₁₂₃₄₅₆₇₈₉', { '₊': '+', '₋': '-' });
const SUPERSCRIPT_RUN = new RegExp(
  `[${Object.keys(SUPERSCRIPT_MAP).join('')}]+`,
  'g'
);
const SUBSCRIPT_RUN = new RegExp(
  `[${Object.keys(SUBSCRIPT_MAP).join('')}]+`,
  'g'
);
const MULTIPLICATION_SIGNS = /[×∗⋅·]/g;
const DIVISION_SIGNS = /[÷∕⁄]/g;
const MINUS_SIGNS = /[−–—]/g; // − – —
const SMART_SINGLE_QUOTES = /[‘’]/g; // ‘ ’
const SMART_DOUBLE_QUOTES = /[“”]/g; // “ ”
const ZERO_WIDTH_CHARS = /[\u200B\uFEFF]/g; // ZWSP, BOM
const NBSP = /\u00A0/g; // non-breaking space

/**
 * Drop the characters that are invisible in the field but change how every
 * later step matches. Text copied from Google Docs or Notion routinely
 * carries a leading zero-width space, and `trim()` doesn't remove it — with
 * the ZWSP still attached, the anchored `$$...$$` match in unwrapEnvelopes
 * fails and the wrapper survives into the stored value.
 *
 * Runs BEFORE unwrapping for that reason; the glyph transliteration below
 * doesn't care about position, so it can stay after.
 */
function stripInvisibleChars(text: string): string {
  return text.replace(ZERO_WIDTH_CHARS, '').replace(NBSP, ' ');
}

/**
 * Rewrite the handful of Unicode math glyphs a copy/paste of *rendered* math
 * (this app's own preview, a Docs/Word equation, a re-copy of a MathLive
 * field) actually produces into the ASCII/LaTeX tokens the on-screen keyboard
 * would have inserted. Idempotent — a second pass over its own output is a
 * no-op, which matters since this runs on both paste and every read.
 *
 * `\sqrt`/`\pi` get a trailing space only when the next character is a
 * letter: a LaTeX command name ends at the first non-letter, so `2πr` would
 * otherwise fuse into the undefined command `2\pir` — an error atom in the
 * editor, and rejected by the grader's compute-engine even though the
 * untransliterated `2πr` parses and grades fine today. Not added before `(`:
 * `\sqrt (x+1)` reads as an empty root followed by `(x+1)`, so `\sqrt(x+1)`
 * must stay tight and go through normalizeExpressionLatex's own `(` -> `{`
 * repair instead.
 */
function transliterateUnicodeMath(text: string): string {
  return text
    .replace(
      SUPERSCRIPT_RUN,
      (run) =>
        `^{${Array.from(run, (ch) => SUPERSCRIPT_MAP[ch] ?? '').join('')}}`
    )
    .replace(
      SUBSCRIPT_RUN,
      (run) => `_{${Array.from(run, (ch) => SUBSCRIPT_MAP[ch] ?? '').join('')}}`
    )
    .replace(MULTIPLICATION_SIGNS, '*')
    .replace(DIVISION_SIGNS, '/')
    .replace(MINUS_SIGNS, '-')
    .replace(/√(?=[a-zA-Z])/g, '\\sqrt ')
    .replace(/√/g, '\\sqrt')
    .replace(/π(?=[a-zA-Z])/g, '\\pi ')
    .replace(/π/g, '\\pi')
    .replace(SMART_SINGLE_QUOTES, "'")
    .replace(SMART_DOUBLE_QUOTES, '"');
}

/**
 * Single normalizer for both write (paste) and read boundaries of a Numeric
 * accepted answer: strip invisible characters, unwrap a delimiter envelope,
 * then transliterate the Unicode math glyphs a paste can carry into the
 * ASCII/LaTeX the equation editor itself would have produced. A no-op for
 * text already in that form.
 */
export function normalizeAnswerInput(text: string): string {
  const raw = text ?? '';
  if (!raw.trim()) {
    return '';
  }
  return transliterateUnicodeMath(
    unwrapEnvelopes(stripInvisibleChars(raw))
  ).trim();
}

/**
 * Read-time tolerance for accepted answers saved before this phase, when the
 * Numeric answer field stored Quill HTML instead of a bare canonical value.
 * Extracts the plain number or LaTeX out of legacy HTML; a no-op for values
 * already stored in the new bare format. Never re-injects the legacy HTML —
 * only ever returns extracted text, which callers render through the
 * sanitized math preview when it classifies as an expression.
 */
export function fromStoredAnswerText(stored: string): string {
  const raw = stored ?? '';
  if (!raw.includes('<')) {
    return normalizeAnswerInput(raw);
  }
  const attrMatch = DATA_LATEX_ATTR.exec(raw);
  if (attrMatch) {
    return normalizeAnswerInput(unescapeHtmlEntities(attrMatch[1]));
  }
  // No math blot — strip tags and any wrapper left by removeEmbed.
  const text = unescapeHtmlEntities(raw.replace(/<[^>]*>/g, ''));
  return normalizeAnswerInput(text);
}

/**
 * The single write-side seam: reduce a Numeric accepted answer to the exact
 * form it should be stored (and validated, and graded) in. Called once, at
 * save time, rather than on every keystroke or every read — so the field can
 * display exactly what's in the store while it's being edited (no display/
 * store split, no fighting the caret) and the DB never holds a value the
 * editor itself wouldn't produce.
 *
 * Idempotent: re-canonicalizing an already-canonical value is a no-op, so
 * calling this on every save (including a save that touches nothing) is
 * always safe.
 *
 * A number is deliberately NOT reduced to parseNumericInput's canonical form
 * — SOC-5969 requires the PDF to print accepted values exactly as the teacher
 * typed them (`3.50` stays `3.50`, not `3.5`).
 */
export function canonicalizeNumericAnswer(text: string): string {
  const value = fromStoredAnswerText(text ?? '');
  return classifyNumericAnswer(value) === 'expression'
    ? normalizeExpressionLatex(value)
    : value;
}
